import { MotesPaths } from './motes-paths';

/** 
 * MediaType determines the type of a Media object. 
 * UNDEFINED = 0, IMAGE = 1, VIDEO = 2, SOUND = 3
 */
export enum MediaType {
    UNDEFINED = 'UNDEFINED',
    IMAGE = 'IMAGE',
    VIDEO = 'VIDEO',
    YOUTUBE_VIDEO = 'YOUTUBE_VIDEO',
    FILMCENTRALEN_VIDEO = 'FILMCENTRALEN_VIDEO',
    SOUND = 'SOUND',
    AMBISONIC = 'AMBISONIC'
}

// TODO: Delete when all media has been converted
export enum MediaTypeDeprecated {
    UNDEFINED = 0,
    IMAGE = 1,
    VIDEO = 2,
    SOUND = 3,
    AMBISONIC = 4
}

type PickEnum<T, K extends T> = {
    [P in keyof K]: P extends K ? P : never;
};

/** 
 * MediaFileType represents all possible media 
 * references in the Media class. Used for controlling references across classes.
 */
export enum MediaFileType {
    ORIGINAL = 'original',
    SQUARE = 'square',
    CROPPED = 'cropped',
    HD = 'hd',
    THUMB = 'thumb',
    PREVIEW = 'preview',
    SOUND = 'sound',
    VIDEO = 'video',
    AMBISONIC = 'ambisonic'
}

export type ImageFileType = PickEnum<MediaFileType,
    MediaFileType.HD |
    MediaFileType.SQUARE |
    MediaFileType.THUMB |
    MediaFileType.PREVIEW
>

export interface Video {
    url: string;
    type: MediaType;
}

/**
 * Define map of allowed media files (to e uploaded) in a media object 
 */
export type AllowedMediaMap = { [key in MediaType]?: boolean };

/**
 * Define reference to a media instance
 * Exposes only the interface needed by MediaFile to avoid circular import reference 
 * and maintain still maintain object integrity if key or file array changes (only a reference to the object is exposed and no properties are copied)   
 */
export interface MediaRef {
    files?: MediaFile[];
    key: string;
    group?: string;
}


/** 
 * ImageFormat are used to specify image formats across components and classes, e.g. FileUploadComponent 
 */
export class ImageFormat {
    constructor(
        public mediaFileType: MediaFileType,
        public maintainAspectRatio: boolean = true,
        public aspectRatio: number = 16 / 9,
        public resizeToWidth: number = 128,
        public autoCrop: boolean = false,
        public hideFile: boolean = false, // Hide the Media file in the list of media files
        public linked: MediaFileType = null, // The linked media file type will always be cropped with the same cropping as parent (but with a different resolution)
        public format: 'png' | 'jpeg' | 'bmp' | 'webp' | 'ico' = "png"
    ) { }
}

/**
 * Map that specifies the format of the different media types of a media object
 */
export type ImageFormatMap = { [key in MediaFileType]?: ImageFormat };

/**
 * Image cropping information 
 */
export interface ImageCropping {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

/**
 * Class representing a media file on the server, including its local path and public url etc.
 */
export class MediaFile {
    // The public url of this media file
    public url: string;
    // Fallback file url if the file cannot be loaded
    public fallback: string;
    // Path to file storage on server
    public storage: string;
    // The specific file type of this MediaFile
    public type: MediaFileType;
    // Generic Media container type associated with the specific filetype
    public mediaType: MediaType;
    // Reference to the containing media element
    public mediaRef: MediaRef;
    // Coordinates describing image cropping af any
    public cropping?: ImageCropping;
    // Image rotation (0 = 0 deg, 1 = 90 deg ...)
    public rotation?: number;
    // Hold file blob content while uploading
    public blob?: File;

    constructor(type: MediaFileType, mediaType: MediaType, mediaRef: MediaRef = null) {
        this.type = type;
        this.mediaType = mediaType;
        // Add this file to the array of files in the media object
        this.mediaRef = mediaRef;
        if(mediaRef) mediaRef.files.push(this);
    }

    public toJson(): any {
        // Delete unused or inconsistent storage paths
        if(!this.url) {
            this.storage = "";
            this.url = "";
            this.cropping = null;
            this.rotation = 0;
        };

        // Convert to flat Media DB format (ie thumb = url, thumb.storage = storage, thumbCropping = {x,y,z})
        let json = {};

        // const invalidDataUrlPlaceholder = "https://firebasestorage.googleapis.com/v0/b/motesdev.appspot.com/o/static-images%2Fsound.png?alt=media&token=1b4a2ad3-0c3d-4ea0-bd88-f826fc4a012c";
        const invalidDataUrlPlaceholder = '';
        json[this.type + ''] = (!this.url || typeof this.url != 'string' || (this.url.startsWith && this.url.startsWith('data:'))) ? invalidDataUrlPlaceholder : this.url || "";
        json[this.type + 'Storage'] = this.storage || "";
        json[this.type + 'Cropping'] = this.cropping || null;
        json[this.type + 'Rotation'] = this.rotation || 0;
        json[this.type + 'Fallback'] = this.fallback || "";
        return json;
    }

    public fromJson(json: any, placeHolder: string = "") {
        let url = json[this.type];
        let proxyUrl = this.proxyUrl(url);

        // Obs placeholder will only be applied if media has any data
        this.url = (proxyUrl) ? this.getGroupUrl(proxyUrl) : '';
        this.storage = json[this.type + 'Storage'] || "";
        this.cropping = json[this.type + 'Cropping'] || null;
        this.rotation = json[this.type + 'Rotation'] || 0;
        this.fallback = json[this.type + 'Fallback'];
    }

    private getGroupUrl(url) {
        if(typeof (url) != 'string' || !this.mediaRef || !this.mediaRef.group || url.indexOf("group=") != -1) return url;

        // Add group to url request if any in order to identify media group in Service Worker or other request context
        url = url.indexOf("?") > 0 ? `${url}&group=${this.mediaRef.group}` : `${url}?group=${this.mediaRef.group}`;
        return url;
    }

    // TODO: Obsolete this
    public proxyUrl(url: string) {
        if(!url) return "";
        // This is a little hackish, how it enables us to inject SafeUrls in Media object
        if((typeof url != 'string')) return url;

        // TODO: Get real mote key in order to save proxy media
        let moteKey = "1001";

        // Use proxy for special requests
        if(url.startsWith("https://www.kulturarv.dk")) {
            return `${MotesPaths.MOTES_REST}/api/proxy?motekey=${moteKey}&url=${url}`;
        }
        // Add full url for functionality across domains
        if(url.startsWith('/api/')) {
            return MotesPaths.MOTES_REST + url;
        }
        return url;
    }
}
